import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Calendar, X, AlertTriangle } from 'lucide-react';
import { DatePicker, Dropdown, Input, MultiDropdown, NumberInput, TextArea } from '../forms';
import type {
  ScheduleTask,
  CreateScheduleTaskRequest,
  UpdateScheduleTaskRequest,
  TaskType,
  TaskStatus,
  TaskPriority,
  DependencyType,
  ConstraintType,
} from '../../types/schedule';

interface ScheduleTaskDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  task?: ScheduleTask | null;
  tasks: ScheduleTask[]; // For dependency selection
  onSubmit: (data: CreateScheduleTaskRequest | UpdateScheduleTaskRequest) => Promise<void>;
  isLoading?: boolean;
}

// Validation schema
const scheduleTaskSchema = yup.object({
  wbsCode: yup
    .string()
    .required('WBS Code is required')
    .max(50, 'WBS Code must be 50 characters or less'),
  name: yup
    .string()
    .required('Task name is required')
    .max(200, 'Name must be 200 characters or less'),
  description: yup.string().max(2000, 'Description must be 2000 characters or less'),
  taskType: yup.string().oneOf(['task', 'milestone', 'summary']).required('Task type is required'),
  plannedStart: yup.date().required('Planned start date is required'),
  plannedEnd: yup
    .date()
    .required('Planned end date is required')
    .min(yup.ref('plannedStart'), 'End date must be after start date'),
  actualStart: yup.date().nullable(),
  actualEnd: yup.date().nullable(),
  plannedDuration: yup.number().min(0, 'Duration must be positive'),
  percentComplete: yup.number().min(0).max(100).required('Progress is required'),
  priority: yup
    .string()
    .oneOf(['low', 'medium', 'high', 'critical'])
    .required('Priority is required'),
  status: yup
    .string()
    .oneOf(['not_started', 'in_progress', 'completed', 'on_hold', 'cancelled'])
    .required('Status is required'),
  predecessorIds: yup.array().of(yup.string()),
  constraintType: yup
    .string()
    .oneOf([
      'none',
      'must_start',
      'must_finish',
      'start_no_earlier',
      'start_no_later',
      'finish_no_earlier',
      'finish_no_later',
    ])
    .nullable(),
  constraintDate: yup.date().nullable(),
  notes: yup.string().max(2000, 'Notes must be 2000 characters or less'),
  parentTaskId: yup.string().nullable(),
});

type FormData = yup.InferType<typeof scheduleTaskSchema>;

const taskTypeOptions = [
  { value: 'task', label: 'Task' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'summary', label: 'Summary Task' },
];

const statusOptions = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'cancelled', label: 'Cancelled' },
];

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const constraintTypeOptions = [
  { value: 'none', label: 'None' },
  { value: 'must_start', label: 'Must Start On' },
  { value: 'must_finish', label: 'Must Finish On' },
  { value: 'start_no_earlier', label: 'Start No Earlier Than' },
  { value: 'start_no_later', label: 'Start No Later Than' },
  { value: 'finish_no_earlier', label: 'Finish No Earlier Than' },
  { value: 'finish_no_later', label: 'Finish No Later Than' },
];

// Dependency type options for future use
const _dependencyTypeOptions = [
  { value: 'FS', label: 'Finish to Start (FS)' },
  { value: 'FF', label: 'Finish to Finish (FF)' },
  { value: 'SS', label: 'Start to Start (SS)' },
  { value: 'SF', label: 'Start to Finish (SF)' },
];

const ScheduleTaskDrawer: React.FC<ScheduleTaskDrawerProps> = ({
  isOpen,
  onClose,
  task,
  tasks,
  onSubmit,
  isLoading = false,
}) => {
  const isEditing = !!task;

  const defaultValues: Partial<FormData> = useMemo(
    () => ({
      wbsCode: '',
      name: '',
      description: '',
      taskType: 'task' as TaskType,
      plannedStart: new Date(),
      plannedEnd: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 1 week from now
      actualStart: undefined,
      actualEnd: undefined,
      plannedDuration: 5,
      percentComplete: 0,
      priority: 'medium' as TaskPriority,
      status: 'not_started' as TaskStatus,
      predecessorIds: [],
      constraintType: 'none' as ConstraintType,
      constraintDate: undefined,
      notes: '',
      parentTaskId: undefined,
    }),
    []
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(scheduleTaskSchema) as any,
    defaultValues,
  });

  // Watch for date changes to calculate duration
  const plannedStart = watch('plannedStart');
  const plannedEnd = watch('plannedEnd');
  const taskType = watch('taskType');
  const constraintType = watch('constraintType');

  // Calculate duration when dates change
  useEffect(() => {
    if (plannedStart && plannedEnd) {
      const start = new Date(plannedStart);
      const end = new Date(plannedEnd);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setValue('plannedDuration', diffDays);
    }
  }, [plannedStart, plannedEnd, setValue]);

  // Reset form when task changes
  useEffect(() => {
    if (task) {
      reset({
        wbsCode: task.wbsCode,
        name: task.name,
        description: task.description || '',
        taskType: task.taskType,
        plannedStart: new Date(task.plannedStart),
        plannedEnd: new Date(task.plannedEnd),
        actualStart: task.actualStart ? new Date(task.actualStart) : undefined,
        actualEnd: task.actualEnd ? new Date(task.actualEnd) : undefined,
        plannedDuration: task.plannedDuration,
        percentComplete: task.percentComplete,
        priority: task.priority,
        status: task.status,
        predecessorIds: task.predecessors?.map(p => p.taskId) || [],
        constraintType: task.constraint?.type || 'none',
        constraintDate: task.constraint?.date ? new Date(task.constraint.date) : undefined,
        notes: task.notes || '',
        parentTaskId: task.parentTaskId || undefined,
      });
    } else {
      reset(defaultValues);
    }
  }, [task, reset, defaultValues]);

  // Build dependency options (exclude current task and its children)
  const dependencyOptions = useMemo(() => {
    return tasks
      .filter(t => t._id !== task?._id)
      .map(t => ({
        value: t._id,
        label: `${t.wbsCode} - ${t.name}`,
      }));
  }, [tasks, task]);

  // Build parent task options (only summary tasks, exclude current task)
  const parentTaskOptions = useMemo(() => {
    const options = [{ value: '', label: 'None (Top Level)' }];
    tasks
      .filter(t => t.taskType === 'summary' && t._id !== task?._id)
      .forEach(t => {
        options.push({
          value: t._id,
          label: `${t.wbsCode} - ${t.name}`,
        });
      });
    return options;
  }, [tasks, task]);

  const onFormSubmit = async (data: FormData) => {
    // Convert Date objects to ISO strings for API request
    const plannedStartStr =
      data.plannedStart instanceof Date
        ? data.plannedStart.toISOString()
        : new Date(data.plannedStart).toISOString();
    const plannedEndStr =
      data.plannedEnd instanceof Date
        ? data.plannedEnd.toISOString()
        : new Date(data.plannedEnd).toISOString();

    const submitData: CreateScheduleTaskRequest | UpdateScheduleTaskRequest = {
      wbsCode: data.wbsCode,
      name: data.name,
      description: data.description,
      taskType: data.taskType as TaskType,
      plannedStart: plannedStartStr,
      plannedEnd: plannedEndStr,
      actualStart: data.actualStart
        ? data.actualStart instanceof Date
          ? data.actualStart.toISOString()
          : new Date(data.actualStart).toISOString()
        : undefined,
      actualEnd: data.actualEnd
        ? data.actualEnd instanceof Date
          ? data.actualEnd.toISOString()
          : new Date(data.actualEnd).toISOString()
        : undefined,
      plannedDuration: data.plannedDuration,
      percentComplete: data.percentComplete,
      priority: data.priority as TaskPriority,
      status: data.status as TaskStatus,
      predecessors: (data.predecessorIds || []).map(id => ({
        taskId: id as string,
        type: 'FS' as DependencyType,
        lag: 0,
      })),
      constraint:
        data.constraintType && data.constraintType !== 'none'
          ? {
              type: data.constraintType as ConstraintType,
              date:
                data.constraintDate instanceof Date
                  ? data.constraintDate
                  : data.constraintDate
                    ? new Date(data.constraintDate)
                    : new Date(),
            }
          : undefined,
      notes: data.notes,
      parentTaskId: data.parentTaskId || undefined,
    };

    await onSubmit(submitData);
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden brand-form">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-2 rounded-lg">
                <Calendar className="h-5 w-5 text-red-500" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Schedule Task' : 'Create Schedule Task'}
              </h2>
              {task?.isCritical && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Critical Path
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Controller
                    name="wbsCode"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="WBS Code"
                        value={field.value}
                        onChange={e => field.onChange(e.target.value)}
                        placeholder="e.g., 1.1.1"
                        required
                        error={errors.wbsCode?.message}
                      />
                    )}
                  />

                  <Controller
                    name="taskType"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Task Type"
                        value={field.value}
                        options={taskTypeOptions}
                        onChange={field.onChange}
                        required
                      />
                    )}
                  />

                  <Controller
                    name="parentTaskId"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Parent Task"
                        value={field.value || ''}
                        options={parentTaskOptions}
                        onChange={field.onChange}
                        placeholder="Select parent..."
                      />
                    )}
                  />
                </div>

                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Task Name"
                      value={field.value}
                      onChange={e => field.onChange(e.target.value)}
                      placeholder="Enter task name"
                      required
                      error={errors.name?.message}
                      maxLength={200}
                    />
                  )}
                />

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label="Description"
                      value={field.value || ''}
                      onChange={field.onChange}
                      placeholder="Describe the task objectives and deliverables..."
                      rows={3}
                      maxLength={2000}
                    />
                  )}
                />
              </div>

              {/* Schedule */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Schedule
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Controller
                    name="plannedStart"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Planned Start"
                        value={
                          field.value ? new Date(field.value).toISOString().split('T')[0] : null
                        }
                        onChange={date => field.onChange(date ? new Date(date) : null)}
                        required
                      />
                    )}
                  />

                  <Controller
                    name="plannedEnd"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Planned End"
                        value={
                          field.value ? new Date(field.value).toISOString().split('T')[0] : null
                        }
                        onChange={date => field.onChange(date ? new Date(date) : null)}
                        required
                        minDate={
                          plannedStart
                            ? new Date(plannedStart).toISOString().split('T')[0]
                            : undefined
                        }
                      />
                    )}
                  />
                </div>

                {taskType !== 'milestone' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Controller
                      name="actualStart"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label="Actual Start"
                          value={
                            field.value ? new Date(field.value).toISOString().split('T')[0] : null
                          }
                          onChange={date => field.onChange(date ? new Date(date) : null)}
                          placeholder="When work actually started"
                        />
                      )}
                    />

                    <Controller
                      name="actualEnd"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label="Actual End"
                          value={
                            field.value ? new Date(field.value).toISOString().split('T')[0] : null
                          }
                          onChange={date => field.onChange(date ? new Date(date) : null)}
                          placeholder="When work actually completed"
                        />
                      )}
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Controller
                    name="plannedDuration"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        label="Duration (Days)"
                        value={field.value || 0}
                        onChange={field.onChange}
                        min={0}
                        disabled={taskType === 'milestone'}
                      />
                    )}
                  />

                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Status"
                        value={field.value}
                        options={statusOptions}
                        onChange={field.onChange}
                        required
                      />
                    )}
                  />

                  <Controller
                    name="priority"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Priority"
                        value={field.value}
                        options={priorityOptions}
                        onChange={field.onChange}
                        required
                      />
                    )}
                  />
                </div>
              </div>

              {/* Progress */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Progress
                </h3>

                <Controller
                  name="percentComplete"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Percent Complete ({field.value}%)
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={field.value}
                        onChange={e => field.onChange(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-brand"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>25%</span>
                        <span>50%</span>
                        <span>75%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  )}
                />
              </div>

              {/* Dependencies */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Dependencies
                </h3>

                <Controller
                  name="predecessorIds"
                  control={control}
                  render={({ field }) => (
                    <MultiDropdown
                      label="Predecessor Tasks"
                      value={field.value || []}
                      options={dependencyOptions}
                      onChange={field.onChange}
                      placeholder="Select predecessor tasks..."
                      maxSelections={20}
                    />
                  )}
                />

                <p className="text-xs text-gray-500">
                  Selected tasks must complete before this task can start (Finish-to-Start
                  relationship).
                </p>
              </div>

              {/* Constraints */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Constraints
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Controller
                    name="constraintType"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Constraint Type"
                        value={field.value || 'none'}
                        options={constraintTypeOptions}
                        onChange={field.onChange}
                      />
                    )}
                  />

                  {constraintType && constraintType !== 'none' && (
                    <Controller
                      name="constraintDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label="Constraint Date"
                          value={
                            field.value ? new Date(field.value).toISOString().split('T')[0] : null
                          }
                          onChange={date => field.onChange(date ? new Date(date) : null)}
                          required
                        />
                      )}
                    />
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Notes
                </h3>

                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label="Notes"
                      value={field.value || ''}
                      onChange={field.onChange}
                      placeholder="Additional notes or comments..."
                      rows={3}
                      maxLength={2000}
                    />
                  )}
                />
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex-shrink-0">
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit(onFormSubmit)}
                disabled={isLoading || !isDirty}
                className="flex items-center rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
              >
                {isLoading && (
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {isEditing ? 'Update Task' : 'Create Task'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ScheduleTaskDrawer;
